import { createPortal } from 'react-dom'
import { useEffect, useState, type ReactNode } from 'react'

/**
 * Fondo oscuro y centrado de un modal, montado directo en el `body`.
 *
 * Un overlay con `fixed inset-0` que vive dentro del árbol de la pantalla no
 * siempre cubre la ventana completa: basta que algún ancestro cree un bloque de
 * contención —un `transform`, un `filter`, un contenedor de consultas— para que
 * el fondo quede recortado al área de contenido. Se veía en el modal de subir
 * facturas: el fondo empezaba debajo del encabezado.
 *
 * Montarlo en el `body` con un portal lo hace inmune a lo que tenga la pantalla
 * por encima, y de paso evita que el modal quede atrapado en un contenedor con
 * scroll.
 */
export const CapaDeModal = ({
  children,
  zIndex = 50,
  onClickFondo,
}: {
  children: ReactNode
  /** Solo se cambia cuando un modal tiene que quedar sobre otro. */
  zIndex?: number
  /** Cerrar al hacer clic fuera del contenido. Se omite si no aplica. */
  onClickFondo?: () => void
}) => {
  // A-18: el fondo se funde y el panel sube mientras escala, 40 ms más lento.
  // La clase se pone en el cuadro siguiente al montaje: si se pinta ya abierto
  // el navegador no tiene de dónde animar y el modal aparece de golpe.
  const [abierto, setAbierto] = useState(false)
  useEffect(() => {
    const cuadro = requestAnimationFrame(() => setAbierto(true))
    return () => cancelAnimationFrame(cuadro)
  }, [])

  return createPortal(
    // Dos capas a propósito: la de afuera pinta el fondo y hace el scroll, la de
    // adentro centra. Con una sola, un modal más alto que la ventana deja sus
    // botones fuera de alcance —montado en el body ya no acompaña el scroll de
    // la página—, y eso pasa en cuanto un modal crece con campos condicionales.
    <div
      className={`capa-modal fixed inset-0 overflow-y-auto ${abierto ? 'abierto' : ''}`}
      style={{ zIndex }}
    >
      <div
        className="panel-modal flex min-h-full items-center justify-center p-4"
        // Solo el clic en el fondo cierra: si el clic viene de algo de adentro,
        // `target` es ese hijo y no esta capa.
        onClick={
          onClickFondo
            ? (e) => {
                if (e.target === e.currentTarget) onClickFondo()
              }
            : undefined
        }
      >
        {children}
      </div>
    </div>,
    document.body,
  )
}

export default CapaDeModal
